<script setup lang="ts">
import { computed, ref, watch, onUnmounted } from "vue";
import { ratingRange, type Field } from "@yawhub/core";
import { StarIcon } from "../icons";
const props = defineProps<{ field: Field }>();
const value = defineModel<string>({ required: true });
const range = computed(() => ratingRange(props.field));
const preview = ref<number | null>(null),
  celebrating = ref<number | null>(null);
let celebrationTimer: ReturnType<typeof setTimeout> | undefined;
watch(value, (next) => {
  clearTimeout(celebrationTimer);
  celebrating.value = next === "" ? null : Number(next);
  celebrationTimer = setTimeout(() => (celebrating.value = null), 420);
});
onUnmounted(() => clearTimeout(celebrationTimer));
const style = computed(() => props.field.ratingStyle ?? "number");
</script>
<template>
  <span class="rating-input">
    <span
      v-if="style === 'stars' || style === 'dots'"
      class="rating-choices"
      :class="{ stars: style === 'stars' }"
      role="group"
      @pointerleave="preview = null"
      :aria-label="field.label"
    >
      <label
        v-for="n in range.max - range.min + 1"
        :key="n"
        class="rating-choice"
        @pointerenter="preview = range.min + n - 1"
        @focusin="preview = range.min + n - 1"
        @focusout="preview = null"
        :class="{
          chosen: value !== '' && Number(value) === range.min + n - 1,
          filled:
            (preview !== null || value !== '') &&
            (preview ?? Number(value)) >= range.min + n - 1,
          celebrating: celebrating === range.min + n - 1,
        }"
      >
        <input
          type="radio"
          :name="'rating-' + field.id"
          :value="String(range.min + n - 1)"
          v-model="value"
          :required="!field.nullable"
          :aria-label="String(range.min + n - 1)"
        />
        <StarIcon
          v-if="style === 'stars' && range.min + n - 1 > 0"
          :size="19"
        />
        <span
          v-else-if="style === 'dots'"
          class="rating-dot"
          aria-hidden="true"
        />
        <span class="rating-label">{{ range.min + n - 1 }}</span>
      </label>
    </span>
    <span v-else-if="style === 'slider'" class="rating-slider">
      <input
        type="range"
        :aria-label="field.label"
        :min="range.min"
        :max="range.max"
        step="1"
        :value="value === '' ? range.min : value"
        @input="value = ($event.target as HTMLInputElement).value"
      />
      <input
        type="number"
        :aria-label="field.label + ' som tal'"
        :min="range.min"
        :max="range.max"
        step="1"
        :required="!field.nullable"
        :value="value"
        @input="value = ($event.target as HTMLInputElement).value"
      />
    </span>
    <input
      v-else
      type="number"
      :aria-label="field.label"
      :min="range.min"
      :max="range.max"
      step="1"
      :required="!field.nullable"
      :value="value"
      @input="value = ($event.target as HTMLInputElement).value"
    />
    <span class="rating-caption"
      ><span
        >{{ value === "" ? "Ikke angivet" : value }} · {{ range.min }}–{{
          range.max
        }}</span
      ><button
        v-if="field.nullable && value !== ''"
        type="button"
        class="text-button"
        @click="value = ''"
      >
        Ryd
      </button></span
    >
  </span>
</template>
<style scoped lang="scss" src="../styles/components/RatingInput.scss"></style>
